// frontend/src/components/ImageHeatmap/ImageHeatmap.jsx

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDynamicTranslation } from './useDynamicTranslation';
import { Tooltip, IconButton, CircularProgress } from '@mui/material';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import './ImageHeatmap.css';

// --- SUB-COMPONENT #1: The content that goes INSIDE the tooltip ---
const HeatmapTooltipContent = ({ reason, severity }) => {
    const { t, i18n } = useTranslation();
    const { 
        textToShow,
        isTranslating,
        isCheckingCache,
        finalTranslation,
        handleTranslate 
    } = useDynamicTranslation(reason);

    const shouldShowTranslateControl = i18n.language !== 'en' && !finalTranslation;

    return (
        <div className="custom-tooltip-content">
            <div className="tooltip-header">
                {shouldShowTranslateControl && (
                    <Tooltip title={t('heatmap.tooltip.translateReason')} placement="right">
                        <span>
                            <IconButton 
                                size="small" 
                                onClick={handleTranslate} 
                                disabled={isCheckingCache || isTranslating}
                                className="translate-tooltip-button"
                            >
                                {isCheckingCache || isTranslating ? <CircularProgress size={14} color="inherit" /> : <GTranslateIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <span>{t('heatmap.tooltip.severity')}: {severity}/10</span>
            </div>
            
            {isTranslating ? (
                <div className="tooltip-reason-loader with-message">
                    <p>{t('resultDisplay.translatingMessage')}</p>
                </div>
            ) : (
                <p className="tooltip-reason">{textToShow}</p>
            )}
        </div>
    );
};


// --- SUB-COMPONENT #2: A stable wrapper for the Tooltip itself ---
const InteractiveTooltip = ({ children, reason, severity }) => {
    return (
        <Tooltip 
            title={<HeatmapTooltipContent reason={reason} severity={severity} />}
            arrow
            placement="top"
            PopperProps={{
                className: 'image-heatmap-tooltip-popper',
                modifiers: [{ name: 'offset', options: { offset: [0, -8] }}],
            }}
            enterDelay={200}
            leaveDelay={100}
        >
            {children}
        </Tooltip>
    );
};


// --- MAIN COMPONENT: Renders the heatmap and uses the stable Tooltip wrapper ---
const ImageHeatmap = ({ regions, imageSrc, mode = 'full' }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const sortedRegions = useMemo(() => {
    if (!regions) return [];


    // 1. Normalize regions to fix AI formatting hallucinations
    const normalizedRegions = regions.map(region => {
        let newBox = region.box;
        let newReason = region.reason;
        
        // If the AI accidentally put the string inside the box array (length 5)
        if (Array.isArray(newBox) && newBox.length > 4 && typeof newBox[4] === 'string') {
            newReason = newReason || newBox[4]; // Extract string to reason
            newBox = newBox.slice(0, 4); // Strip the box down to just 4 coordinates
        }
        
        return {
            ...region,
            box: newBox,
            reason: newReason
        };
    });

    // 2. Sort by area descending so smaller boxes render on top of bigger ones
    return normalizedRegions.sort((a, b) => {
        if (!a.box || a.box.length < 4 || !b.box || b.box.length < 4) return 0;

        const areaA = (a.box[2] - a.box[0]) * (a.box[3] - a.box[1]);
        const areaB = (b.box[2] - b.box[0]) * (b.box[3] - b.box[1]);
        return areaB - areaA;
    });
  }, [regions]);

  if (!regions || regions.length === 0) {
    return null;
  }

  const clippedImageStyle = {};
  if (mode === 'full' && hoveredIndex !== null && sortedRegions[hoveredIndex]) {
    const { box } = sortedRegions[hoveredIndex];
    if (box && box.length === 4) {
      const [x_min, y_min, x_max, y_max] = box;
      clippedImageStyle.clipPath = `inset(${y_min * 100}% ${100 - (x_max * 100)}% ${100 - (y_max * 100)}% ${x_min * 100}%)`;
    }
  }
  
  const containerClassName = `heatmap-overlay-container ${mode === 'full' && hoveredIndex !== null ? 'is-hovered' : ''}`;

  return (
    <div className={containerClassName}>
      {mode === 'full' && (
        <>
          <div className="heatmap-blur-layer"></div>
          <img
            src={imageSrc}
            className="heatmap-clipped-image"
            style={clippedImageStyle}
            alt={t('heatmap.clearRegionViewAlt')}
          />
        </>
      )}

      <div className="heatmap-box-container">
        {sortedRegions.map((region, index) => {
          const { box, reason, severity } = region;
          if (!box || box.length !== 4) return null;

          const style = {
            left: `${box[0] * 100}%`,
            top: `${box[1] * 100}%`,
            width: `${(box[2] - box[0]) * 100}%`,
            height: `${(box[3] - box[1]) * 100}%`,
          };

          const boxClassName = `heatmap-box ${mode === 'full' && hoveredIndex !== null && hoveredIndex !== index ? 'dimmed' : ''}`;

          return (
            <InteractiveTooltip key={index} reason={reason} severity={severity}>
              <div
                className={boxClassName}
                style={style}
                onMouseEnter={(e) => { e.stopPropagation(); setHoveredIndex(index); }}
                onMouseLeave={(e) => { e.stopPropagation(); setHoveredIndex(null); }}
              />
            </InteractiveTooltip>
          );
        })}
      </div>
    </div>
  );
};

export default ImageHeatmap;